import Chart, {ChartConfiguration, ChartDataSets, ChartXAxe, ChartYAxe} from 'chart.js';
import React, {MutableRefObject, useEffect, useRef, useState} from 'react';
import {Button} from '~/components/Button';
import {MultiSelectInput} from '~/components/Controls/Select';
import {ISelectItem} from '~/components/Controls/Select/common';
import {IntentText} from '~/components/Form';
import {useAbility} from '~/lib/ability';
import {Intent} from '~/lib/intent';
import {DashboardAbility} from '~/modules/Dashboard/ability';
import {
    getPerformanceField,
    getVisibleFieldsWithStorage,
    performanceFields,
    performanceValue,
    setVisibleFieldsWithStorage,
    toGranularityDateString
} from '~/modules/Dashboard/lib/performance';
import {Granularity, IPerformance} from '~/modules/Dashboard/models/Performance';
import {ChartType, useDashboardReducer} from '~/modules/Dashboard/reducer';
import style from './index.module.scss';

const chartBody = (canvas: MutableRefObject<HTMLCanvasElement>) => <div className={style.chartBody}>
    <canvas ref={canvas} className={style.canvas}/>
</div>;

const yAxeId = (index: number) => `y-axe-${index}`;

const yAxe = (field: keyof IPerformance, index: number, short: boolean, chartYAxe?: ChartYAxe): ChartYAxe => {
    const performanceField = getPerformanceField(field);
    return ({
        ...chartYAxe,
        id: yAxeId(index),
        position: (index % 2 === 0) ? 'left' : 'right',
        scaleLabel: {
            display: true,
            fontColor: performanceField.color,
            labelString: performanceField.title,
        },
        ticks: {
            callback: (value) => Math.floor(value) === value ? performanceValue(value, short) : null,
        },
        gridLines: {
            drawOnChartArea: false
        },
    });
};

const ySeries = (field: keyof IPerformance, index: number, data: IPerformance[], chartDataSets?: ChartDataSets): ChartDataSets => {
    const performanceField = getPerformanceField(field);
    return ({
        ...chartDataSets,
        label: performanceField.title,
        yAxisID: yAxeId(index),
        data: data.map(performance => performanceField.chartValue
            ? performanceField.chartValue(performance[field] as number)
            : performance[field] as number),
        borderColor: performanceField.color,
        backgroundColor: performanceField.color,
        pointRadius: 4,
        spanGaps: false,
        borderWidth: 1
    });
};

const xAxe = (chartYAxe: ChartYAxe = {}): ChartXAxe => ({
    ...chartYAxe,
    ticks: {
        autoSkip: true,
        maxTicksLimit: 22,
    }
});

interface IGenericChartProps {
    data: IPerformance[];
    granularity: Granularity;
    fields: (keyof IPerformance)[];
    short?: boolean;
}

type ILineChartProps = IGenericChartProps;

export const LineChart = ({data, fields, granularity, short}: ILineChartProps) => {
    const canvas = useRef<HTMLCanvasElement>();
    const [chart, setChart] = useState<Chart>(null);

    const generateConfig = (): ChartConfiguration => ({
        type: 'line',
        options: {
            tooltips: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    label: (tooltipItem, {datasets}) =>
                        `${datasets[tooltipItem.datasetIndex].label}: ${performanceValue(tooltipItem.yLabel as number, short)}`
                }
            },
            animation: {
                duration: 0
            },
            scales: {
                type: 'time',
                yAxes: fields.map((field, index) => yAxe(field, index, short)),
                xAxes: [xAxe()]
            },
            maintainAspectRatio: false,
            legend: {
                display: false,
                position: 'top',
                onClick: null,
                labels: {
                    usePointStyle: false
                }
            }
        },
        data: {
            labels: data.map(performance => toGranularityDateString(new Date(performance.timestamp), granularity)),
            datasets: fields.map((field, index) => ySeries(field, index, data, {
                pointBorderColor: 'white',
                pointRadius: 3,
                borderWidth: 1,
                fill: false
            }))
        }
    });
    useEffect(() => {
        if (canvas.current) {
            if (chart) {
                chart.destroy();
            }
            setChart(new Chart(canvas.current, generateConfig()));
        }
    }, [fields, short]);
    return chartBody(canvas);
};

type IBarChartProps = IGenericChartProps & {
    stacked?: boolean;
    formatLabel?: (date: Date) => string;
    compact?: boolean;
    highlight?: number;
    mark?: {index: number; value: number};
};

type BarElement = {_model: {x: number; width: number}; _yScale: {getPixelForValue: (value: number) => number}};

const markPlugin = (mark: {index: number; value: number}): Chart.PluginServiceRegistrationOptions => ({
    afterDatasetsDraw: chart => {
        const bar = chart.getDatasetMeta(0).data[mark.index] as unknown as BarElement | undefined;
        const context = chart.ctx;
        if (!bar || !context) {
            return;
        }
        const {x, width} = bar._model;
        const y = bar._yScale.getPixelForValue(mark.value);
        context.save();
        context.strokeStyle = 'red';
        context.lineWidth = 2;
        context.beginPath();
        context.moveTo(x - width / 2 - 2, y);
        context.lineTo(x + width / 2 + 2, y);
        context.stroke();
        context.restore();
    }
});

const withHighlight = (dataSet: ChartDataSets, highlight?: number): ChartDataSets => {
    if (highlight === undefined) {
        return dataSet;
    }
    const color = dataSet.backgroundColor as string;
    const darker = Chart.helpers.color(color).darken(0.35).rgbString();
    const colors = (dataSet.data ?? []).map((_, index) => index === highlight ? darker : color);
    return {...dataSet, backgroundColor: colors, borderColor: colors};
};

const compactYAxe = (axe: ChartYAxe, suggestedMax?: number): ChartYAxe => ({
    ...axe,
    scaleLabel: {display: false},
    ticks: {
        beginAtZero: true,
        suggestedMax,
        maxTicksLimit: 3,
        callback: (value) => `$${performanceValue(Number(value), true)}`
    }
});

const compactXAxe = (axe: ChartXAxe): ChartXAxe => ({
    ...axe,
    gridLines: {display: false},
    ticks: {
        autoSkip: true,
        maxTicksLimit: 8,
        maxRotation: 0
    }
});

export const BarChart = ({data, fields, stacked, granularity, short, formatLabel, compact, highlight, mark}: IBarChartProps) => {
    const canvas = useRef<HTMLCanvasElement>();
    const [chart, setChart] = useState<Chart>(null);
    const yAxes = fields.map((field, index) => yAxe(field, index, short, {stacked}));
    const xAxes = [xAxe({stacked})];
    const generateConfig = (): ChartConfiguration => ({
        type: 'bar',
        options: {
            tooltips: {
                mode: 'index',
                intersect: false,
                ...(compact && {callbacks: {label: item => String(item.yLabel)}})
            },
            animation: {
                duration: 0
            },
            scales: {
                type: 'time',
                yAxes: compact ? yAxes.map(axe => compactYAxe(axe, mark?.value)) : yAxes,
                xAxes: compact ? xAxes.map(compactXAxe) : xAxes
            },
            maintainAspectRatio: false,
            legend: {
                display: false,
                position: 'top',
                onClick: null,
                labels: {
                    usePointStyle: false
                }
            }
        },
        data: {
            labels: data.map(performance => {
                const date = new Date(performance.timestamp);
                return formatLabel ? formatLabel(date) : toGranularityDateString(date, granularity);
            }),
            datasets: fields.map((field, index) => withHighlight(ySeries(field, index, data, {
                fill: false,
                ...(compact && {barPercentage: 0.85, categoryPercentage: 1})
            }), highlight))
        },
        plugins: mark ? [markPlugin(mark)] : []
    });
    useEffect(() => {
        if (canvas.current) {
            if (chart) {
                chart.destroy();
            }
            setChart(new Chart(canvas.current, generateConfig()));
        }
    }, [fields, stacked, short]);
    return compact
        ? <div className={style.compactBody}><canvas ref={canvas}/></div>
        : chartBody(canvas);
};

const mergePerformance = (a: IPerformance, b: IPerformance): IPerformance => {
    const result = ({
        adRequests: a.adRequests + b.adRequests,
        clicks: a.clicks + b.clicks,
        errors: a.errors + b.errors,
        exchangeFee: a.exchangeFee + b.exchangeFee,
        finishes: a.finishes + b.finishes,
        impressions: a.impressions + b.impressions,
        lostImpressions: a.lostImpressions + b.lostImpressions,
        lostImpressionsRevenue: a.lostImpressionsRevenue + b.lostImpressionsRevenue,
        spent: a.spent + b.spent,
        sspIncome: a.sspIncome + b.sspIncome,
        timestamp: a.timestamp,
        country: null,
        adType: null,
        platform: null,
        agency: null,
        seller: null,
        payer: null
    }) as IPerformance;
    result.ctr = ((result.impressions > 0 ? (result.clicks / result.impressions) : 0) * 100);
    result.ecpm = (result.impressions > 0 ? result.sspIncome / (result.impressions / 1000) : 0);
    return result;
};

const mergeByTimestamp = (data: IPerformance[]): IPerformance[] => data.reduce<IPerformance[]>((result, record) => {
    const foundedIndex = result.findIndex(findRecord => record.timestamp === findRecord.timestamp);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    foundedIndex >= 0
        ? result[foundedIndex] = mergePerformance(result[foundedIndex], record)
        : result.push({
            ...record,
            country: null,
            adType: null,
            platform: null,
            agency: null,
            seller: null,
            payer: null
        });
    return result;
}, []);

export const Charts = () => {
    const ability = useAbility(DashboardAbility);
    const {state: {data, query, table: {short}}, setChartView} = useDashboardReducer();
    const [fields, setFields] = useState<(keyof IPerformance)[]>(getVisibleFieldsWithStorage('charts', ['impressions', 'clicks']));
    const mergedData = mergeByTimestamp(data);
    return mergedData.length > 0 && <div className={style.chart}>
        {fields.length > 0 ? <>
            {query.chartType === ChartType.DIMENSION &&
            <BarChart data={mergedData} fields={fields} granularity={query.granularity} short={short}/>}
            {query.chartType === ChartType.TOTAL &&
            <LineChart data={mergedData} fields={fields} granularity={query.granularity} short={short}/>}
        </> : <IntentText intent={Intent.DANGER} className={style.chartMessage} children='No metrics selected'/>}
        <div className={style.chartToolbar}>
            <div className={style.chartTypes}>
                <Button leftIcon={<i className='fa fa-line-chart'/>}
                    intent={query.chartType === ChartType.TOTAL ? Intent.PRIMARY : Intent.DEFAULT}
                    onClick={() => setChartView(ChartType.TOTAL)}/>
                <Button leftIcon={<i className='fa fa-bar-chart'/>}
                    intent={query.chartType === ChartType.DIMENSION ? Intent.PRIMARY : Intent.DEFAULT}
                    onClick={() => setChartView(ChartType.DIMENSION)}/>
            </div>
            <div className={style.chartOptions}>
                <MultiSelectInput value={fields}
                    className={style.chartMetrics}
                    placeholder='select one or more metrics'
                    onChange={visibleFields => setVisibleFieldsWithStorage(visibleFields, 'charts', setFields)}
                    items={performanceFields
                        .filter(field =>
                            field.tableCell &&
                                          !field.isDictionary &&
                                          (!field.performanceType || field.performanceType === query.performanceType) &&
                                          !(field.hideByAbility && field.hideByAbility(ability)))
                        .map<ISelectItem<keyof IPerformance>>(field => ({
                        label: field.title,
                        value: field.key
                    }))}/>
            </div>
        </div>
    </div>;
};
